import React, { useState } from 'react';
import { ChevronDown, AlertCircle } from 'lucide-react';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const dayNum = (d) => Number(d.slice(8, 10));
const initials = (n) => (n || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

// Status → badge text + colour (matches the prototype: DONE / ASSIGNED / LIVE
// NOW / UNASSIGNED / MISSED).
const STATUS_BADGE = {
  unassigned: { t: 'Unassigned', c: 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-300' },
  scheduled: { t: 'Assigned', c: 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' },
  completed: { t: 'Done', c: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/10 dark:text-emerald-400' },
  in_progress: { t: 'Live now', c: 'bg-sky-100 text-sky-600 dark:bg-sky-900/20 dark:text-sky-300' },
  missed: { t: 'Missed', c: 'bg-rose-100 text-rose-600 dark:bg-rose-900/20 dark:text-rose-300' },
};
const CARD_TONE = {
  unassigned: 'border-purple-200 bg-purple-50/70 dark:border-purple-900/50 dark:bg-purple-900/20',
  scheduled: 'border-blue-200 bg-blue-50/50 dark:border-blue-900/50 dark:bg-blue-900/20',
  completed: 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-900/10',
  in_progress: 'border-sky-200 bg-sky-50/60 dark:border-sky-900/50 dark:bg-sky-900/20',
  missed: 'border-rose-200 bg-rose-50/60 dark:border-rose-900/50 dark:bg-rose-900/20',
};
// Darker left-accent bar colour per status.
const CARD_ACCENT = {
  unassigned: '#a855f7',
  scheduled: '#2563eb',
  completed: '#10b981',
  in_progress: '#0ea5e9',
  missed: '#ef4444',
};

// A rich visit card — package name + status badge, time range, and
// "client · zone · travel area".
const VisitCard = ({ v, zone, area, day, onOpen, draggable, onDragStart }) => {
  const b = STATUS_BADGE[v.status] || STATUS_BADGE.scheduled;
  const subtitle = [v.client_name || v.carer_name || (v.status === 'unassigned' ? 'Unassigned' : '—'), zone?.name, area].filter(Boolean).join(' · ');
  return (
    <div
      role="button"
      tabIndex={0}
      draggable={!!draggable}
      onDragStart={draggable ? (e) => onDragStart(e, v) : undefined}
      onClick={() => onOpen(v, day)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(v, day); } }}
      style={{ borderLeftColor: CARD_ACCENT[v.status] || CARD_ACCENT.scheduled }}
      className={`block w-full overflow-hidden rounded-lg border border-l-4 px-2 py-1.5 text-left transition hover:brightness-95 ${CARD_TONE[v.status] || CARD_TONE.scheduled} ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="truncate text-[11px] font-bold text-gray-800 dark:text-gray-200">{v.package_name}</span>
        <span className={`flex-shrink-0 rounded px-1 py-0.5 text-[7px] font-bold uppercase ${b.c}`}>{b.t}</span>
      </div>
      <div className="mt-0.5 text-[10px] font-semibold text-gray-600 dark:text-gray-400">{v.start}{v.end ? `–${v.end}` : ''}</div>
      <div className="truncate text-[9px] text-gray-400 dark:text-gray-500">{subtitle}</div>
    </div>
  );
};

const cols = { gridTemplateColumns: '180px repeat(7, minmax(150px, 1fr))' };

const ZoneWeek = ({ zone, days, view, onOpen, onAssign }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [dropKey, setDropKey] = useState(null); // `${carerId}:${day}` being hovered
  const rows = view === 'client' ? zone.clients : zone.carers;
  const area = zone.postcode_prefixes?.length ? zone.postcode_prefixes.join('/') : null;
  const unassignedDays = zone.unassigned_days || {};
  const hasUnassigned = view === 'carer' && Object.keys(unassignedDays).length > 0;
  const canDrop = view === 'carer' && !!onAssign;

  const onDragStart = (e, v) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ assignment_id: v.assignment_id, zoneId: zone.id ?? null }));
  };
  const handleDrop = (e, carerId, day) => {
    e.preventDefault();
    setDropKey(null);
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData('text/plain')); } catch (_) { return; }
    if (!payload || payload.assignment_id == null) return;
    // Only allow dropping onto a carer in the SAME zone.
    if ((payload.zoneId ?? null) !== (zone.id ?? null)) return;
    onAssign(payload.assignment_id, carerId, day);
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#262b33] shadow-sm">
      <header className="flex items-center justify-between gap-4 border-b border-gray-100 dark:border-gray-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setCollapsed((c) => !c)} className="grid h-7 w-7 place-items-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]">
            <ChevronDown className={`h-4 w-4 transition ${collapsed ? '-rotate-90' : ''}`} />
          </button>
          <span className="h-3 w-3 rounded-full" style={{ background: zone.color || '#2563eb' }} />
          <div>
            <div className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">{zone.name}</div>
            <div className="text-[11px] text-gray-400 dark:text-gray-500">{area ? `Travel area · ${area}` : 'No travel area'}</div>
          </div>
        </div>
      </header>

      {!collapsed && (
        <div className="overflow-x-auto">
          <div style={{ minWidth: 180 + 7 * 150 }}>
            {/* Day header */}
            <div className="grid bg-gray-50/60 dark:bg-[#20242c]" style={cols}>
              <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">{view === 'client' ? 'Client' : 'Carer / availability'}</div>
              {days.map((d, i) => (
                <div key={d} className="border-l border-gray-100 dark:border-gray-800 py-2 text-center text-[10px] font-semibold text-gray-400 dark:text-gray-500">{DOW[i]} {dayNum(d)}</div>
              ))}
            </div>

            {/* Unassigned row FIRST (carer view) — drag a card onto a carer below */}
            {hasUnassigned && (
              <div className="grid bg-purple-50/30 dark:bg-purple-900/10" style={cols}>
                <div className="flex items-center gap-2 border-t border-r border-gray-100 dark:border-gray-800 px-3 py-2">
                  <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full bg-purple-100 dark:bg-purple-900/20 text-purple-500 dark:text-purple-300"><AlertCircle className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold text-gray-800 dark:text-gray-200">Unassigned visits</div>
                    {canDrop && <div className="text-[9px] text-gray-400 dark:text-gray-500">Drag onto a matching carer</div>}
                  </div>
                </div>
                {days.map((d) => (
                  <div key={d} className="min-h-[58px] space-y-1 border-l border-t border-gray-50 dark:border-gray-800 p-1">
                    {(unassignedDays[d] || []).map((v, i) => (
                      <VisitCard key={`${v.assignment_id}-${i}`} v={v} zone={zone} area={area} day={d} onOpen={onOpen} draggable={canDrop} onDragStart={onDragStart} />
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* Rows */}
            {(rows || []).map((row) => (
              <div key={row.id} className="grid" style={cols}>
                <div className="flex items-center gap-2 border-t border-r border-gray-100 dark:border-gray-800 px-3 py-2">
                  <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg bg-blue-50 dark:bg-blue-900/20 text-[10px] font-bold text-blue-700 dark:text-blue-400">
                    {view === 'client' ? initials(row.name) : row.initials}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[12px] font-semibold text-gray-800 dark:text-gray-200">{row.name}</div>
                    <div className="truncate text-[9px] text-gray-400 dark:text-gray-500">{view === 'client' ? (row.postcode || row.code || '') : `Covers ${zone.name}`}</div>
                  </div>
                </div>
                {days.map((d) => {
                  const key = `${row.id}:${d}`;
                  const dropProps = canDrop ? {
                    onDragOver: (e) => { e.preventDefault(); setDropKey(key); },
                    onDragLeave: () => setDropKey((k) => (k === key ? null : k)),
                    onDrop: (e) => handleDrop(e, row.id, d),
                  } : {};
                  return (
                    <div
                      key={d}
                      {...dropProps}
                      className={`min-h-[58px] space-y-1 border-l border-t border-gray-50 dark:border-gray-800 p-1 ${dropKey === key ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-inset ring-blue-300' : ''}`}
                    >
                      {(row.days?.[d] || []).map((v, i) => (
                        <VisitCard key={`${v.assignment_id}-${i}`} v={v} zone={zone} area={area} day={d} onOpen={onOpen} />
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const PackageWeekView = ({ zones = [], days = [], view, onOpen, onAssign }) => {
  if (zones.length === 0) {
    return <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-800 bg-white dark:bg-[#262b33] px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">No care packages this week.</div>;
  }
  return (
    <div className="flex flex-col gap-4">
      {zones.map((z) => <ZoneWeek key={z.id ?? 'unzoned'} zone={z} days={days} view={view} onOpen={onOpen} onAssign={onAssign} />)}
    </div>
  );
};

export default PackageWeekView;
